import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import mergeObjects from 'lodash/merge';

import {TItem as TParametersItem} from '../Parameters/definitions';
import {TAddressItem} from '../Subscribers/definitions';
import {TDialogSubscriber, TDialogSubscriberResponse} from './definitions';
import {getSubscriberName} from './utils';

type TSubscribersByAddress = Record<string, Record<string, TDialogSubscriber>>;

const initialState = () => {
  return {
    subscribers: {} as TSubscribersByAddress,
  };
};

const dialogsSlice = createSlice({
  name: 'dialogs',
  initialState,
  reducers: {
    setSubscribers(state, action: PayloadAction<TDialogSubscriberResponse[]>) {
      const subscribersByAddress = action.payload.reduce((result, subscriber) => {
        const parametersByCode = subscriber.parameterValues
          ?.filter((parameter) => parameter.code && parameter.value)
          .reduce(
            (parametersResult, parameter) => {
              parametersResult[parameter.code as string] = parameter;

              return parametersResult;
            },
            {} as Record<string, TParametersItem>,
          );

        const addressesByType = subscriber.addresses.reduce(
          (addressesResult, address) => {
            addressesResult[address.type] = address;

            return addressesResult;
          },
          {} as Record<string, TAddressItem>,
        );

        subscriber.addresses.forEach((address) => {
          result[address.type] = {
            ...(result[address.type] ?? {}),
            [String(address.address)]: {
              id: subscriber.subscriberId,
              name: getSubscriberName(addressesByType, parametersByCode),
              addresses: addressesByType,
              parameters: parametersByCode,
            },
          };
        });

        return result;
      }, {} as TSubscribersByAddress);

      state.subscribers = mergeObjects(state.subscribers, subscribersByAddress);
    },
  },
});

export const {setSubscribers: setSubscribersAction} = dialogsSlice.actions;

export const selectSubscriberByAddress =
  (subscriberAddress?: string, subscriberAddressType?: string) => (state: TRootState) =>
    subscriberAddress && subscriberAddressType
      ? state.dialogs.subscribers[subscriberAddressType]?.[subscriberAddress]
      : null;

export default dialogsSlice.reducer;
